/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useEffect, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { AVAILABLE_PROMOCODES, FREE1MONTH, propTypes } from '../../../../util/types';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  InlineTextButton,
  Modal,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import { freePlan, getUserRole, userSubscriptions } from '../../../../util/helper';
import { useSelector } from 'react-redux';

const TopbarMobileMenu = React.memo(props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    notificationCount,
    onLogout,
  } = props;
  const [isUserHasSubscribed, setIsUserHasSubscribed] = useState(false);
  const [isTopbarModal, setIsTopbarModal] = useState(false);
  // Memoize expensive computations
  const user = useMemo(() => ensureCurrentUser(currentUser), [currentUser]);

  const userInfo = useMemo(() => {
    const ensuredCurrentUser = ensureCurrentUser(currentUser);
    const isFreePlan = !!freePlan(ensuredCurrentUser);
    const userSubscription = !!userSubscriptions(ensuredCurrentUser)?.length;
    const userRole = currentUser && currentUser?.id && getUserRole(currentUser);
    return { ensuredCurrentUser, isFreePlan, userSubscription, userRole };
  }, [currentUser]);

  const profileData = useMemo(() => {
    const { publicData, protectedData } = currentUser?.attributes?.profile || {};
    const { bookmarks, total_listings, availableProducts } = publicData || {};
    const { userSubscriptionData, promoCode } = protectedData || {};

    const cartListings = bookmarks?.map(st => st.id);
    const lengthOfCart = cartListings?.length > 0 ? cartListings?.length : null;
    const isSubscriptionActive = userSubscriptionData ? true : false;
    const isFoodDrinkMerchant = availableProducts == 'food_Drinks';
    const isValidPromoCode = AVAILABLE_PROMOCODES.some(code => promoCode?.includes(code));
    const is30DaysFree = promoCode == FREE1MONTH;

    return {
      bookmarks, total_listings, availableProducts, userSubscriptionData, promoCode,
      cartListings, lengthOfCart, isSubscriptionActive, isFoodDrinkMerchant,
      isValidPromoCode, is30DaysFree
    };
  }, [currentUser?.attributes?.profile]);
  const currentUserListing = useSelector(state => state?.user?.userListing?.attributes?.publicData);
  const { businessDescription, announcements, typeOfProducts, primaryPhotoId } =
    currentUser?.attributes?.profile?.publicData || {};
  const profileModal = businessDescription && primaryPhotoId && announcements;
  
  useEffect(() => {
    if (userInfo.isFreePlan || userInfo.userSubscription) {
      setIsUserHasSubscribed(true);
    }
  }, [userInfo.isFreePlan, userInfo.userSubscription])

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="WelcomePage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>
        </div>
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink1" />
          </NamedLink>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>
        <NamedLink
          className={classNames(css.inbox, currentPageClass('InboxPage'))}
          name="InboxPage"
          params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
        >
          <FormattedMessage id="TopbarMobileMenu.inboxLink" />
          {notificationCountBadge}
        </NamedLink>
        <NamedLink
          className={classNames(css.inbox, currentPageClass('ContactUsPage'))}
          name="ContactUsPage"
        >
          <FormattedMessage id="TopbarMobileMenu.ContactLink" />
        </NamedLink>
        {userInfo.userRole && !profileData.isFoodDrinkMerchant && (
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.yourListingsLink1" />
          </NamedLink>
        )}
        {userInfo.userRole && (
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
        )}
        <span className={css.cardBox}>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('CartPage'))}
            name="CartPage"
          >
            <FormattedMessage id="TopbarMobileMenu.cartitemLink" />
            {profileData.lengthOfCart ? <span className={css.cartCountMobile}>{profileData.lengthOfCart}</span> : null}
          </NamedLink>
        </span>
        {userInfo.userRole &&
          ((!profileData.isSubscriptionActive||!profileModal) ? (
            <div
              type="button"
              onClick={() => setIsTopbarModal(true)}
              className={classNames(css.navigationLink, currentPageClass('CreateCouponPage'))}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.createCoupon" />
            </div>
          ) : (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('CreateCouponPage'))}
              name="CreateCouponPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.createCoupon" />
            </NamedLink>
          ))}

        <a
          className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
          href={userInfo.userRole ? '/account/payments' : '/account/contact-details'}
        >
          {userInfo.userRole ? (
            <FormattedMessage id="TopbarDesktop.accountSettingsLinkMerchant" />
          ) : (
            <FormattedMessage id="TopbarDesktop.accountSettingsLinkCustomer" />
          )}
        </a>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>
        {userInfo.userRole && !profileData.isFoodDrinkMerchant && (
          <div className={css.profileApplyButton}>
            {(!profileData.isSubscriptionActive ||!profileModal) ? (
              <div
                type="button"
                onClick={() => setIsTopbarModal(true)}
                className={classNames(css.yourListingsLink, currentPageClass('NewListingPage'))}
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.newListing" />
              </div>
            ) : profileData.total_listings && profileData.total_listings >= 9 ? (
              <div
                type="button"
                onClick={() => setIsTopbarModal(true)}
                className={classNames(css.yourListingsLink, currentPageClass('NewListingPage'))}
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.newListing" />
              </div>
            ) : (
              <NamedLink
                className={classNames(css.yourListingsLink, currentPageClass('NewListingPage'))}
                name="NewListingPage"
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.newListing" />
              </NamedLink>
            )}
          </div>
        )}
      </div>
      <div>
        {!profileModal ? (
          <Modal
            className={css.subscribeFirstModal}
            id="TopbarCreateListing"
            isOpen={isTopbarModal}
            onClose={() => setIsTopbarModal(false)}
            onManageDisableScrolling={() => { }}
          >
            <div className={css.termsWrapper}>
              <h4><FormattedMessage id="TopbarMobileMenu.completeProfile" /></h4>
              <NamedLink className={css.applyButton} name="ProfileSettingsPage">
                <FormattedMessage id="TopbarMobileMenu.goToProfile" />
              </NamedLink>
            </div>
          </Modal>
        ) : !profileData.isSubscriptionActive ? (
          <Modal
            className={css.subscribeFirstModal}
            id="TopbarCreateListing"
            isOpen={isTopbarModal}
            onClose={() => setIsTopbarModal(false)}
            onManageDisableScrolling={() => { }}
          >
            <div className={css.termsWrapper}>
              <h3 className={css.subscriptionModalHeading}>
                <FormattedMessage id="TopbarMobileMenu.subscriptionRequiredTitle" />
              </h3>
              {profileData.is30DaysFree ? (
                <p className={css.subscriptionModalText}>
                  <FormattedMessage id="TopbarMobileMenu.subscribe30DayspromoCode" />
                </p>
              ) : profileData.isValidPromoCode ? (
                <p className={css.subscriptionModalText}>
                  <FormattedMessage id="TopbarMobileMenu.subscribeFirstwithPromoCode" />
                </p>
              ) : (
                <p className={css.subscriptionModalText}>
                  <FormattedMessage id="TopbarMobileMenu.subscribeFirst" />
                </p>
              )}
              <NamedLink className={css.applyButton} name="StripeSubscriptionPage">
                <FormattedMessage id="TopbarMobileMenu.goToSubscription" />
              </NamedLink>
            </div>
          </Modal>
        ) : (
          profileData.total_listings &&
          profileData.total_listings >= 9 && (
            <Modal
              id="TopbarCreateListing"
              isOpen={isTopbarModal}
              onClose={() => setIsTopbarModal(false)}
              onManageDisableScrolling={() => { }}
            >
              <div className={css.termsWrapper}>
                <h4><FormattedMessage id="TopbarMobileMenu.exceededLimit" /></h4>
                <NamedLink className={css.applyButton} name="ManageListingsPage">
                  <FormattedMessage id="TopbarMobileMenu.goToYourListing" />
                </NamedLink>
              </div>
            </Modal>
          )
        )}
      </div>
    </div>
  );
});

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
